import { useState } from 'react';
import { Building2, Check, ExternalLink, Loader2, UserRound, X } from 'lucide-react';
import { toast } from 'react-toastify';
import { organizationService, type OrganizationQueueItem } from '../services/organizationService';

function relativeDate(iso: string | null): string {
  if (!iso) return 'unknown date';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return 'unknown date';
  const mins = Math.round((Date.now() - then) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}

/** Safe hostname for the link label — a malformed stored URL shouldn't throw. */
function hostnameOf(url: string): string {
  try {
    return new URL(/^https?:\/\//i.test(url) ? url : `https://${url}`).hostname;
  } catch {
    return url;
  }
}

/**
 * One employer domain awaiting a human decision.
 *
 * The reviewer's real question is "is this a place someone actually works?",
 * so the card leads with the evidence the lookup gathered and what the student
 * claimed — not with the bare domain, which answers nothing on its own.
 *
 * A domain only reaches this queue when the automated check was inconclusive.
 * That is usually a small real business with a thin web presence, not a fake,
 * so the buttons are framed as a judgement about the employer rather than an
 * accusation about the student.
 */
export default function EmployerReviewCard({ item, onDecided }: { item: OrganizationQueueItem; onDecided: () => void }) {
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState<'confirm' | 'reject' | null>(null);

  const decide = async (confirm: boolean) => {
    if (busy) return;
    setBusy(confirm ? 'confirm' : 'reject');
    try {
      const res = await organizationService.decide(item.domain, confirm, note.trim() || undefined);
      toast.success(
        confirm
          ? res.promoted > 0
            ? `Confirmed — ${res.promoted} endorsement${res.promoted === 1 ? '' : 's'} now verified.`
            : 'Confirmed. Endorsements from this domain verify automatically from now on.'
          : 'Rejected — endorsements from this domain stay unverified.',
      );
      onDecided();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Couldn't record that decision");
      setBusy(null);
    }
  };

  return (
    <div className={`rounded-2xl border overflow-hidden flex flex-col ${item.pendingHypes > 0 ? 'border-amber-200' : 'border-gray-200'}`}>
      <div className="flex-1 flex flex-col gap-3 p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <Building2 className="w-4 h-4 text-gray-400 shrink-0" />
              <p className="text-sm font-bold text-gray-900 truncate">{item.name || item.domain}</p>
            </div>
            {item.name && <p className="text-xs text-gray-500 truncate">{item.domain}</p>}
          </div>
          <span className="shrink-0 text-[11px] text-gray-400">{relativeDate(item.checkedAt)}</span>
        </div>

        {/* Why this row is worth doing now rather than later. */}
        {item.pendingHypes > 0 && (
          <div className="inline-flex items-center gap-1.5 self-start rounded-full bg-amber-50 border border-amber-200 px-2.5 py-1">
            <UserRound className="w-3.5 h-3.5 text-amber-700" />
            <span className="text-[11px] font-bold text-amber-800">
              {item.pendingHypes} endorsement{item.pendingHypes === 1 ? '' : 's'} waiting on this
            </span>
          </div>
        )}

        {item.summary && <p className="text-sm text-gray-700 leading-snug">{item.summary}</p>}

        {/* What the student said the place was. A mismatch is worth a second
            look but is weak on its own — franchises, parent companies and
            trading names all produce honest mismatches. */}
        {item.statedOrganizations.length > 0 && (
          <div className="rounded-lg bg-gray-50 border border-gray-200 px-3 py-2">
            <p className="text-[11px] font-bold text-gray-500 uppercase tracking-wide">Student said</p>
            <p className="mt-0.5 text-xs text-gray-700">{item.statedOrganizations.join(' · ')}</p>
          </div>
        )}

        {item.lookupNote && (
          <div className="rounded-lg bg-blue-50 border border-blue-200 px-3 py-2">
            <p className="text-[11px] font-bold text-blue-900 uppercase tracking-wide">What the check found</p>
            <p className="mt-0.5 text-xs text-blue-900/90 leading-snug">{item.lookupNote}</p>
            {typeof item.confidence === 'number' && <p className="mt-1 text-[10px] text-blue-900/60">Confidence {Math.round(item.confidence * 100)}%</p>}
          </div>
        )}

        {item.website && (
          <a
            href={item.website}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 self-start text-xs font-semibold text-blue-600 hover:text-blue-700"
          >
            <ExternalLink className="w-3.5 h-3.5" />
            Open {hostnameOf(item.website)}
          </a>
        )}

        <div className="mt-auto flex flex-col gap-2">
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            maxLength={500}
            rows={2}
            placeholder="Optional note — kept with the decision for the next reviewer"
            className="w-full rounded-lg border border-gray-200 px-2.5 py-2 text-xs outline-none focus:border-primary resize-none"
          />
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => decide(true)}
              disabled={busy !== null}
              className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {busy === 'confirm' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
              Real employer
            </button>
            <button
              type="button"
              onClick={() => decide(false)}
              disabled={busy !== null}
              className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-white border border-red-200 text-red-700 text-sm font-semibold hover:bg-red-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {busy === 'reject' ? <Loader2 className="w-4 h-4 animate-spin" /> : <X className="w-4 h-4" />}
              Not verified
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
